import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Guided walkthrough: a floating card that steps through the app, navigating to
// the real page each slide describes so users learn where things live, not just
// what they do. Fires automatically the first time a mode (buyer/seller) is
// entered on this device (localStorage), and replays from the header's ? button.

export const TUTORIAL_KEY = (mode) => `lanebids-tutorial-${mode}`;

const BUYER_SLIDES = [
  {
    route: '/buyer/deals',
    title: 'Available Deals',
    body: 'Deals that selling dealers send to your lot show up here. Each one is a set of cars with an end date — bidding stays open through the end of that day.',
  },
  {
    route: '/buyer/deals',
    title: 'Bidding on a car',
    body: 'Open a deal and tap a car to expand its photos, condition, grade, and disclosure. Place a bid, or use Buy Now to take it at the asking price. If the seller counters, you can accept or counter back.',
  },
  {
    route: '/buyer/pending',
    title: 'Purchases',
    body: 'An accepted bid becomes a purchase here. The step tracker shows what happens next — the seller sets the order of payment, inspection, and pickup for each deal. Blue buttons are your moves.',
  },
  {
    route: '/buyer/pending',
    title: 'Payment and title',
    body: 'Payment happens directly between dealerships, off the platform. Mark your payment sent when it is, and watch for "Title received" once the seller hands over the title.',
  },
  {
    route: '/settings',
    title: 'Settings',
    body: 'Manage your lot and team here. Share your lot code with a seller and they can start sending you deals. Email notifications can be tuned per category.',
  },
];

const SELLER_SLIDES = [
  {
    route: '/seller/deals',
    title: 'Your deals',
    body: 'A deal is a package of cars offered to buyer lots you choose. Drafts stay private until you send them.',
  },
  {
    route: '/seller/deals/new',
    title: 'Building a deal',
    body: 'Set the end date, inspection window, and transaction steps — the order of payment, inspection, and pickup for every sale on this deal. Add cars fresh or pull them from inventory. Nothing saves until you say so.',
  },
  {
    route: '/seller/transactions',
    title: 'Bids and sales',
    body: 'Review incoming bids — accept, reject, or counter. Accepted bids become sales here: confirm payment, mark the car ready for pickup, and check off the title once the buyer has it.',
  },
  {
    route: '/seller/settings',
    title: 'Connecting buyers',
    body: 'Under Buyers, enter a buying dealer\'s lot code to connect — then their lot appears as a recipient when you send deals. Your own lots and team live here too.',
  },
];

const SLIDES = { buyer: BUYER_SLIDES, seller: SELLER_SLIDES };

export default function Tutorial({ mode, onClose }) {
  const [idx, setIdx] = React.useState(0);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const slides = SLIDES[mode] || BUYER_SLIDES;
  const slide = slides[idx];
  const last = idx === slides.length - 1;
  const arrivedRef = React.useRef(false);

  // Take the user to the page this slide is talking about. Depends on idx only —
  // re-navigating on every pathname change would fight the user's own clicks.
  useEffect(() => {
    arrivedRef.current = false;
    if (slide.route && pathname !== slide.route) navigate(slide.route);
    else arrivedRef.current = true;
  }, [idx]); // eslint-disable-line

  // Once we've landed on the slide's own route, any further pathname change
  // means the user navigated away on their own (e.g. clicked a nav link) —
  // close the tour instead of leaving it stranded on the wrong page.
  useEffect(() => {
    if (slide.route && pathname === slide.route) arrivedRef.current = true;
    else if (arrivedRef.current) onClose();
  }, [pathname]); // eslint-disable-line

  const finish = () => {
    localStorage.setItem(TUTORIAL_KEY(mode), '1');
    onClose();
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex justify-center p-3 sm:p-6 pointer-events-none">
      <div className="pointer-events-auto w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-2xl ring-1 ring-slate-200 dark:ring-slate-600 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
              {mode === 'seller' ? 'Seller tour' : 'Buyer tour'} · {idx + 1}/{slides.length}
            </p>
            <h2 className="mt-1 text-base font-bold text-slate-900 dark:text-white">{slide.title}</h2>
          </div>
          <button
            onClick={finish}
            aria-label="Close tutorial"
            className="shrink-0 -m-1 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{slide.body}</p>

        <div className="mt-4 flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            {slides.map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? 'bg-blue-600 dark:bg-blue-400' : 'bg-slate-200 dark:bg-slate-600'}`} />
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {idx > 0 && (
              <button onClick={() => setIdx(i => i - 1)} className="btn-secondary text-xs px-3 py-1.5">
                Back
              </button>
            )}
            <button
              onClick={() => (last ? finish() : setIdx(i => i + 1))}
              className="btn-primary text-xs px-3 py-1.5"
            >
              {last ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
